// BackToTop.js — a round button in the bottom-right corner that scrolls back
// to the top, shown once the page has been scrolled about two screens down
// (long pages: clubs, head to heads, seasons, records). Out of the tab order
// and the accessibility tree while it's hidden.

import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import KeyboardArrowUpRounded from "@mui/icons-material/KeyboardArrowUpRounded";
import { BRAND } from "../theme";

export default function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    let frame = 0;
    const check = () => {
      frame = 0;
      setShow(window.scrollY > window.innerHeight * 2);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(check);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    check();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  const toTop = () => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };
  return (
    <Box
      component="button"
      type="button"
      onClick={toTop}
      aria-label="Back to top"
      title="Back to top"
      tabIndex={show ? 0 : -1}
      aria-hidden={show ? undefined : true}
      sx={{
        position: "fixed",
        right: { xs: 16, sm: 24 },
        bottom: { xs: "calc(16px + env(safe-area-inset-bottom))", sm: 24 },
        zIndex: 30,
        width: 44,
        height: 44,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "#fff",
        background: "rgba(24,24,30,0.9)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        border: `1px solid ${BRAND.divider}`,
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        opacity: show ? 1 : 0,
        transform: show ? "none" : "translateY(12px)",
        pointerEvents: show ? "auto" : "none",
        transition: "opacity 160ms ease-out, transform 160ms ease-out",
        "&:hover": { borderColor: BRAND.accent, color: BRAND.accent },
        "&:focus-visible": { outline: `2px solid ${BRAND.accent}`, outlineOffset: 2 },
      }}
    >
      <KeyboardArrowUpRounded />
    </Box>
  );
}
